import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, DialogActions, DialogContent } from "@mui/material";
import { Dialog } from "primereact/dialog";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Claim, UserManageForm, UserManagePayload, UserManageTableEntry } from "./interfaces";
import { Customer } from "../shared/interfaces/Customer";
import { UserRoles } from "./UserRolesEnum";
import { RegistrationAlert, SnackSeverity } from "../register/SnackbarComponents";
import { Snackbar } from "../shared/Snackbar";
import { useUpdateClaim } from "./useUpdateClaim";
import { User } from "../shared/interfaces/User";
import {
  getUserRoleFromStringValue,
  isCompanySiteEmpty,
  userRoleStringRemoveSpaceAndCaptialiseFirstLetter,
} from "./utilities";
import CircularProgress from "@mui/material/CircularProgress";
import { useCustomers } from "../data/useCustomers";
import { UserManageDetailTable } from "./UserManageDetailTable";
import { Region } from "../shared/interfaces/Region";
import { v4 as uuid } from "uuid";
import { useClaimUserRoles } from "./useClaimUserRoles";
import { useQueryClient } from "react-query";
import { UserRolesConstants } from "../shared/constants/UserRolesConstants";

interface UserDetailModalProps {
  user: User | undefined;
  show: boolean;
  onClose: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  width: "80%",
  maxWidth: "none",
  maxHeight: "none",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const StyledCircularProgressDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
}));

const getEntryKey = (entry: { customer?: Customer; company?: Region; site?: Region; userRole?: UserRoles }) =>
  `${entry.customer?.id}-${entry.company?.id}-${entry.site?.id}-${entry.userRole}`;

export function UserDetailModal(props: UserDetailModalProps) {
  const { t } = useTranslation();
  const { user, show, onClose } = props;
  const [userManageTableEntries, setUserManageTableEntries] = useState<UserManageTableEntry[]>([]);
  const [initialFetch, setInitialFetch] = useState<boolean>(true);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({ open: false, message: "", severity: SnackSeverity.SUCCESS });

  const queryClient = useQueryClient();

  const updateClaim = useUpdateClaim(handleSuccess);

  const customers = useCustomers();

  const { data, isLoading, isError } = useClaimUserRoles(user?.userName);

  const handleGetClaimsSuccess = useCallback(
    (claimsDetails: UserManageTableEntry[] | null) => {
      if (!claimsDetails) {
        return;
      }

      if (initialFetch) {
        setUserManageTableEntries((prevEntry) => {
          return [...prevEntry, ...claimsDetails].map((userManageTableEntry) => {
            return {
              ...userManageTableEntry,
              rowId: uuid(),
              userRole: getUserRoleFromStringValue(userManageTableEntry.userRole),
              isCustomerValid: true,
              isUserRoleValid: true,
              isDuplicate: false,
            };
          });
        });
        setInitialFetch(false);
      }
    },
    [initialFetch],
  );

  function handleSuccess() {
    setSnackbar({
      open: true,
      message: "User has been updated successfully",
      severity: "success",
    });
  }

  useEffect(() => {
    if (isError) {
      setSnackbar({
        severity: "error",
        message: t("userManagement.getClaimsError"),
        open: true,
      });
    }
  }, [t, isError]);

  useEffect(() => {
    if (data) {
      handleGetClaimsSuccess(data);
    }
  }, [data, handleGetClaimsSuccess]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleValidFormSubmission = () => {
    setInitialFetch(false);
    const isValid = validateForm(userManageTableEntries);
    if (!isValid) return;
    let userManagePayload: UserManagePayload = {
      email: user?.userName ?? "",
      claims: [],
    };
    userManageTableEntries.forEach((userManageTableEntry) => {
      const userManageForm: UserManageForm = {
        email: user?.userName,
        userRole: userManageTableEntry.userRole,
        customerId: userManageTableEntry.customer?.id,
        company: userManageTableEntry.company,
        site: userManageTableEntry.site,
      };

      const updatedUserRole = convertUserRoleToClaim(userManageForm);

      if (updatedUserRole != null) {
        userManagePayload.claims.push(updatedUserRole);
      }
    });

    updateClaim.mutate(userManagePayload, {
      onSuccess: () => {
        handleSuccess();
        queryClient.invalidateQueries(["userRoles", user?.userName]);
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          message: t("userManagement.updateClaimError"),
          open: true,
        });
      },
    });
  };

  function handleModalClose() {
    setUserManageTableEntries([]);
    setInitialFetch(true);
    onClose();
  }

  const isCustomerValid = (customer: Customer | undefined) => {
    return customer != null;
  };

  const isUserRoleValid = (userRole: UserRoles | undefined) => {
    return userRole != null;
  };

  const validateForm = (userUserManageTableEntries: UserManageTableEntry[]) => {
    let allValid = true;
    const seenKeys = new Map();

    const newEntries = userUserManageTableEntries.map((userManageTableEntry) => {
      let userRoleValid = isUserRoleValid(userManageTableEntry.userRole);
      let customerValid =
        userManageTableEntry.userRole === UserRoles.SuperAdmin ? true : isCustomerValid(userManageTableEntry.customer);

      const entryKey = getEntryKey(userManageTableEntry);
      let isDuplicate = seenKeys.has(entryKey);

      if (isDuplicate) {
        isDuplicate = true;
        allValid = false;
      } else {
        seenKeys.set(entryKey, true);
      }

      allValid = allValid && customerValid && userRoleValid;

      return {
        ...userManageTableEntry,
        isCustomerValid: customerValid,
        isUserRoleValid: userRoleValid,
        isDuplicate: isDuplicate,
      };
    });

    setUserManageTableEntries(newEntries);

    return allValid;
  };

  function convertUserRoleToClaim(data: UserManageForm): Claim | undefined {
    let userRole = "";

    if (data.userRole != null) {
      userRole = userRoleStringRemoveSpaceAndCaptialiseFirstLetter(data.userRole);
    }

    if (data.userRole === UserRoles.SuperAdmin.toString()) {
      return {
        type: `ManagedSurveys${userRole}`,
        value: `ManagedSurveys${userRole}`,
      };
    }

    if (data.customerId == null) {
      return undefined;
    }

    if (isCompanySiteEmpty(data.company as Region)) {
      let customer = { type: `Customer${userRole}`, value: data.customerId };
      if (data.userRole === UserRoles.AdminUser.toString()) {
        customer.type = UserRolesConstants.CustomerUTPAdmin;
      }
      return customer;
    }

    if (isCompanySiteEmpty(data.site as Region)) {
      return { type: `Company${userRole}`, value: data.company?.id };
    }

    return { type: `Site${userRole}`, value: data.site?.id };
  }

  function handleAddClaimClick() {
    setUserManageTableEntries((prevEntries) => {
      return [
        ...prevEntries,
        {
          rowId: uuid(),
          userRole: undefined,
          customer: customers && customers.data && customers.data.length === 1 ? customers.data[0] : undefined,
          company: undefined,
          site: undefined,
          isCustomerValid: true,
          isUserRoleValid: true,
        },
      ];
    });
  }

  return (
    <Card>
      <StyledDialog
        header={
          <Typography variant="h5" component="p">{`${t("userManagement.manageUser")} (${user?.userName})`}</Typography>
        }
        visible={show}
        onHide={handleModalClose}
        baseZIndex={1000}
      >
        <DialogContent>
          {isLoading ? (
            <StyledCircularProgressDiv>
              <CircularProgress size={50} />
            </StyledCircularProgressDiv>
          ) : (
            <UserManageDetailTable
              userManageTableEntries={userManageTableEntries}
              onUpdatedUserManageTableEntries={setUserManageTableEntries}
              onAddClaimClick={handleAddClaimClick}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleModalClose}>
            {t("common.close")}
          </Button>
          <Button variant="contained" onClick={handleValidFormSubmission}>
            {t("common.save")}
          </Button>
        </DialogActions>
        <Snackbar open={snackbar.open} message={snackbar.message} onClose={handleSnackbarClose}>
          <RegistrationAlert onClose={handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </RegistrationAlert>
        </Snackbar>
      </StyledDialog>
    </Card>
  );
}
